import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  useAllCalendars,
  type CalendarWithRole,
} from './AllCalendarsContext';
import {
  calendarTheme,
  type CalendarTheme,
} from '../lib/calendarTheme';

interface CurrentCalendarContextValue {
  calendar: CalendarWithRole | undefined;
  color: string;
  theme: CalendarTheme;
}

const CurrentCalendarContext =
  createContext<CurrentCalendarContextValue | null>(null);

export function CurrentCalendarProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { calendarId } = useParams<{
    calendarId: string;
  }>();
  const { getCalendar, getCalendarColor } =
    useAllCalendars();

  const color = calendarId
    ? getCalendarColor(calendarId)
    : '#4a5568';

  const value = useMemo(
    () => ({
      calendar: calendarId
        ? getCalendar(calendarId)
        : undefined,
      color,
      theme: calendarTheme(color),
    }),
    [calendarId, getCalendar, getCalendarColor, color]
  );

  return (
    <CurrentCalendarContext.Provider value={value}>
      {children}
    </CurrentCalendarContext.Provider>
  );
}

export function useCurrentCalendar(): CurrentCalendarContextValue {
  const context = useContext(CurrentCalendarContext);
  if (!context) {
    throw new Error(
      'useCurrentCalendar must be used within CurrentCalendarProvider'
    );
  }
  return context;
}

// Lightweight hook that just returns the color
export function useCurrentCalendarColor(): string {
  return useCurrentCalendar().color;
}

// Lightweight hook that just returns the theme
export function useCurrentCalendarTheme(): CalendarTheme {
  return useCurrentCalendar().theme;
}
